import type { Exercise } from "../api/types";

/** Cibles `data-attention` des Données initiales désignées par le lien unique « Ouvrir les Données initiales »
 *  (R12, R13). Mêmes noms que ceux de l'accueil (`dossier-level`, `dossier-camp`, `dossier-name`), complétés de
 *  l'ordre de bataille, de la mission, du lieu du nouveau terrain et de l'ennemi (étapes balisées). */
export type MissionDataField =
  | "dossier-level"
  | "dossier-camp"
  | "dossier-place"
  | "dossier-odb"
  | "dossier-mission"
  | "dossier-name"
  | "dossier-centac-rotation"
  | "dossier-weather"
  | "dossier-enemy"
  | "dossier-drones";

/** Champ du numéro de la rotation CENTAC (« NN/AA », registre A119), demandé avant la rédaction de l'OPO N°1. */
export const CENTAC_ROTATION_FIELD = "dossier-centac-rotation" satisfies MissionDataField;

/** Réglage « Météo de l'exercice » (WeatherField), ouvert depuis les modes d'action et les constats de cohérence. */
export const WEATHER_FIELD = "dossier-weather" satisfies MissionDataField;

/** Section « Drones » des Données initiales (chantier « Drones ») : ouverte depuis les constats de cohérence drones ;
 *  sa désignation ouvre d'abord la `Disclosure` « Régler l'emploi des drones », puis désigne le réglage. */
export const DRONES_FIELD = "dossier-drones" satisfies MissionDataField;

/** Événement de fenêtre émis avant la désignation de `DRONES_FIELD` (amendement A2) : la section « Drones » l'écoute
 *  et ouvre sa `Disclosure` « Régler l'emploi des drones », pour que le réglage désigné soit visible. `detail` :
 *  `{ target?: string }`, cible `data-attention` précise à désigner dans la section (sinon la section entière). */
export const OPEN_DRONES_SETTINGS = "generex:drones-settings";

/** Cibles `data-attention` des réglages de la section « Drones » (`DronesField`), désignables par
 *  `OPEN_DRONES_SETTINGS` (`detail.target`) : une seule table, lue par la section et par les constats de cohérence
 *  (`detail.setting` du serveur : régime, bulle de brouillage, caméra thermique). */
export const DRONES_TARGETS = {
  regime: "dossier-drones-regime",
  vaa: "dossier-drones-vaa",
  mto: "dossier-drones-mto",
  jamming: "dossier-drones-jamming",
  thermal: "dossier-drones-thermal",
} as const;

/** Cible `data-attention` d'un réglage drone nommé par le serveur (`detail.setting`), ou undefined : inconnu. */
export function dronesSettingTarget(setting: unknown): string | undefined {
  return typeof setting === "string" && Object.hasOwn(DRONES_TARGETS, setting)
    ? DRONES_TARGETS[setting as keyof typeof DRONES_TARGETS]
    : undefined;
}

/** Ce que le lien doit faire :
 *  - `field` : ouvrir les Données initiales et désigner ce champ ;
 *  - `save` : demander l'enregistrement (`requestSave`), qui désigne le nom ou « Créer le dossier » / « Enregistrer » ;
 *  - `view` : ouvrir les Données initiales seulement (rien ne manque). */
export type MissionDataTarget = { kind: "field"; field: MissionDataField } | { kind: "save" } | { kind: "view" };

/** Champ manquant d'un dossier enregistré : le niveau de l'ordre, puis le lieu (le camp, ou le lieu nommé d'un
 *  nouveau terrain qui reste à placer), puis l'ordre de bataille. */
export function missingDossierField(exercise: Pick<Exercise, "map_slug" | "parameters"> | null | undefined): MissionDataField | null {
  if (!exercise) return null;
  if (!exercise.parameters.level) return "dossier-level";
  if (!exercise.map_slug) return exercise.parameters.place ? "dossier-place" : "dossier-camp";
  if (!exercise.parameters.odb) return "dossier-odb";
  return null;
}

/** Arbitrage du lien « Ouvrir les Données initiales » :
 *  1. aucun dispositif affiché (le brouillon n'a pas de camp) : le champ manquant du dossier, à défaut le camp ;
 *  2. dispositif d'un brouillon non enregistré (dossier à créer, ou modifications en cours) : l'enregistrement —
 *     le brouillon a alors niveau, camp et ordre de bataille, seul l'enregistrement manque ;
 *  3. dossier enregistré et inchangé : son champ manquant, sinon le champ propre au panneau (`fallback`, par
 *     exemple la mission quand aucune unité amie n'en a), sinon la vue seule.
 *  `deployed` : un dispositif est affiché (`deploymentParams`) ; `saved` : ce dispositif est celui du dossier
 *  enregistré (`deploymentParams.exercise_id`). */
export function missionDataTarget({
  exercise,
  deployed,
  saved,
  fallback,
}: {
  exercise?: Pick<Exercise, "map_slug" | "parameters"> | null;
  deployed: boolean;
  saved: boolean;
  fallback?: MissionDataField;
}): MissionDataTarget {
  const missing = missingDossierField(exercise);
  if (!deployed) return { kind: "field", field: missing ?? "dossier-camp" };
  if (!saved) return { kind: "save" };
  const field = missing ?? fallback;
  return field ? { kind: "field", field } : { kind: "view" };
}
